import { isDefined } from "@clipboard-health/util-ts";
import { useGetQuery, type UseGetQueryOptions } from "@src/appV2/api";
import { useDebounce } from "@src/appV2/lib/utils";
import { type UseQueryResult } from "@tanstack/react-query";
import { z } from "zod";

import { environmentConfig } from "../environment";
import { ExtraWorkedTimeType } from "../ExtraWorkedTimeRequests/types";

// FIXME: This object is incomplete
const facilityUserDetailsResponseSchema = z.object({
  type: z.string(),
  _id: z.string(),
  deactivated: z.boolean().nullish(),
  tmz: z.string(),
  email: z.string(),
  facility: z
    .object({
      _id: z.string(),
      userId: z.string(),
      name: z.string(),
      status: z.string(),
      requiresLunchBreak: z.boolean(),
      extraWorkedTimeConfig: z
        .object({
          [ExtraWorkedTimeType.BREAK]: z
            .object({
              isAutoDeclinationEnabled: z.boolean().optional(),
              autoDeclinationReason: z.string().nullish(),
            })
            .optional(),
        })
        .optional(),

      placementSettings: z
        .object({
          managedByParentWorkplace: z.boolean().optional(),
          isProspect: z.boolean().optional(),
        })
        .optional(),

      isTestAccount: z.boolean(),
      parentFacilityId: z.string().optional(),
    })
    .optional(),
});

export type FacilityUserDetailsResponse = z.infer<typeof facilityUserDetailsResponseSchema>;

export function useGetFacilityUserDetails(
  userId?: string
): UseQueryResult<FacilityUserDetailsResponse> {
  return useGetQuery({
    url: `${environmentConfig.REACT_APP_BASE_API_URL}/user/get/${userId}`,
    responseSchema: facilityUserDetailsResponseSchema,
    meta: {
      logErrorMessage: "Fetching facility user details failed.",
    },
    enabled: isDefined(userId),
  });
}

const facilitySearchProfile = z.object({
  _id: z.string(),
  name: z.string(),
  userId: z.string(),
});

export type FacilitySearchProfile = z.infer<typeof facilitySearchProfile>;
export const facilitySearchSchema = z.array(facilitySearchProfile);
export type FacilityUserSearchResponse = z.infer<typeof facilitySearchSchema>;

export function useGetFacilityTeamMembers(
  facilityId: string,
  options?: UseGetQueryOptions<FacilityUserSearchResponse>
): UseQueryResult<FacilityUserSearchResponse> {
  return useGetQuery({
    url: `${environmentConfig.REACT_APP_BASE_API_URL}/facilityProfile/list`,
    responseSchema: facilitySearchSchema,
    meta: {
      logErrorMessage: "Error fetching facility team members.",
    },
    queryParams: {
      filter: { facilityId },
      projection: ["userId", "name"],
      onlyActive: true,
    },
    staleTime: 60 * 1000 * 5,
    enabled: Boolean(facilityId),
    ...options,
  });
}

export function useSearchFacilityUsers(
  params: {
    name: string;
    onlyActive: boolean;
    facilityId?: string;
  },
  options?: UseGetQueryOptions<FacilityUserSearchResponse>
): UseQueryResult<FacilityUserSearchResponse> {
  const { name, facilityId, onlyActive } = params;
  const nameDebounced = useDebounce({ debounceTimeInMs: 500, value: name });
  return useGetQuery({
    url: `${environmentConfig.REACT_APP_BASE_API_URL}/facilityProfile/list`,
    responseSchema: facilitySearchSchema,
    meta: {
      logErrorMessage: "Error fetching the facility info.",
    },
    queryParams: {
      filter: { name: nameDebounced, facilityId },
      projection: ["userId", "name"],
      onlyActive,
    },
    staleTime: 60 * 1000 * 2,
    enabled: nameDebounced?.length > 0,
    ...options,
  });
}
